import * as services from "../services";
import * as data from "../data";
import * as utils from "../utils";
import * as apis from "./apis";
import * as actionTypes from "../constants";
import { clientRequest } from "./index";

const colorNames = [
  "blue",
  "green",
  "orange",
  "yellow",
  "purple",
  "brown",
  "red",
  "maroon",
  "azure",
  "violet",
  "charcoal",
  "rose",
];

//--------------------------------------------------------- AUTHENTICATE MODULE ----------------------------------------------------------------
// Post Payloads
// RegisterUserRequest (RegisterUser/RegisterCommunity): {firstName, lastName, email, password}
export const registerPayload = (form) => {
  return {
    firstName: form.name,
    lastName: "",
    email: form.email,
    password: form.password,
  };
};

export const loginPayload = (UserName, Password) => {
  return {
    UserName,
    Password,
  };
};

// Get Payloads

//--------------------------------------------------------- UPLOAD MODULE ----------------------------------------------------------------
// Post Payloads
export const familiesPayload = () => {
  return {
    User: {
      Email: "admin@cosmaonline.com",
    },
  };
};

// photoUrl is expected to already be uploaded (via uploadPhoto()) by the
// time this runs — FamilyDto stores a URL/key, not raw file bytes.
export const familyPayload = ({ id, name, relationship, phoneNumber, email, photoUrl }) => {
  return {
    id: id && !isNaN(id) ? id : 0,
    userId: services.getUser(),
    name,
    relationship,
    phoneNumber,
    email,
    photoUrl,
  };
};

// EventDto now has photoUrl (added alongside Family/Item's) — expected
// already-uploaded (via uploadPhoto()) by the time this runs, same pattern.
export const eventPayload = ({ id, title, description, eventDate, location, photoUrl }) => {
  return {
    id: id && !isNaN(id) ? id : 0,
    userId: services.getUser(),
    title,
    description,
    eventDate,
    location,
    photoUrl,
  };
};

// ItemDto has no color/colour-table concept anymore — just a free-text
// category. The color picker UI still works (cosmaresource/colours is
// unchanged), its selection now just fills the category field instead.
// photoUrl is expected to already be uploaded (via uploadPhoto()).
export const itemPayload = ({ id, name, description, category, colorName, photoUrl }) => {
  return {
    id: id && !isNaN(id) ? id : 0,
    userId: services.getUser(),
    name,
    description,
    category: category || colorName,
    photoUrl,
  };
};

export const reminderPayload = ({
  medicationName,
  frequency,
  startDate,
  startTime,
  count,
  remindMeBefore,
}) => {
  return {
    MedicatonName: medicationName,
    Frequency: frequency,
    StartDate: startDate,
    StartTime: startTime,
    Often: 2,
    Repeat: count,
    RemindMeBefore: remindMeBefore.split(" ")[0],
    UserId: services.getUser(),
  };
};

export const behaviouralPayload = (form) => {
  const toScore = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  return {
    id: form.id || 0,
    userId: services.getUser(),
    date: new Date().toISOString(),
    sleepScore: toScore(form.sleepScore),
    foodScore: toScore(form.foodScore),
    moodScore: toScore(form.moodScore),
    activityScore: toScore(form.activityScore),
  };
};

// CreateBehaviouralDto: the detailed journal form (meal times, sleep,
// exercise, hobbies) — a separate resource from the simple score above.
export const behaviouralFormPayload = (form) => {
  return {
    userId: services.getUser(),
    breakfastIntakeTime: form.breakfastIntakeTime || "",
    lunchIntakeTime: form.lunchIntakeTime || "",
    dinnerIntakeTime: form.dinnerIntakeTime || "",
    otherIntakeTime: form.otherIntakeTime || "",
    sleepTime: form.sleepTime || "",
    sleepDuration: form.sleepDuration || "",
    sleeper: form.sleeper || "",
    exercise: form.exercise || "",
    walk: form.walk || "",
    miles: form.miles || "",
    medication: form.medication || "",
    hobbies: form.hobbies || "",
  };
};

// ScheduleDto dropped the Time/Task lookup-table model entirely — it's now
// direct title/startTime/endTime/description fields, no dropdown selection.
export const schedulePayload = ({ id, title, startTime, endTime, description }) => {
  return {
    id: id && !isNaN(id) ? id : 0,
    userId: services.getUser(),
    title,
    startTime,
    endTime,
    description,
  };
};

// SongDto has no raw-file field — url is expected to already point at an
// uploaded file. No dedicated audio-upload endpoint exists on dementia, so
// this reuses the shared uploadPhoto() (a generic S3 file store despite the
// name) for the audio file too. Untested against a live upload — flag if
// the backend rejects non-image content.
export const songPayload = ({ id, title, artist, url, thumbnailUrl }) => {
  return {
    id: id && !isNaN(id) ? id : 0,
    userId: services.getUser(),
    title,
    artist,
    url,
    thumbnailUrl,
  };
};

// Get Payloads
export const getPagination = (data) => {
  return {
    page: data.page,
    pageSize: data.pageSize,
    pageCount: data.pageCount,
  };
};

// Uploads a photo blob to S3 via the backend and returns the stored key/url.
// Response shape isn't documented in the OpenAPI spec (untested against a
// live upload) — this defensively handles the response being a bare string
// or an object with a url/key/photoUrl field.
export const uploadPhoto = async (blob, filename = "photo.jpg") => {
  const formData = new FormData();
  formData.append("file", blob, filename);
  const res = await clientRequest(actionTypes.POST, apis.UPLOAD_PHOTO_API, formData);
  const body = res.data;
  if (typeof body === "string") return body;
  return body?.url || body?.key || body?.photoUrl || body;
};

// The photo bucket is private, so a stored key needs a temporary presigned
// URL to actually be viewable. If it's already a full URL, use it as-is.
export const getPhotoViewUrl = async (photoUrl) => {
  if (!photoUrl) return data.getPlaceHolder();
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  try {
    const res = await clientRequest(actionTypes.POST, apis.GET_PRESIGNED_URL_API, {
      key: photoUrl,
    });
    const body = res.data;
    return (typeof body === "string" ? body : body?.url) || data.getPlaceHolder();
  } catch {
    return data.getPlaceHolder();
  }
};

export const getPhoto = () => {
  return data.getPlaceHolder();
};

export const getFamiliesPayload = async (arr) => {
  let res = [];
  for (let i = 0; i < arr.length; i++) {
    const photo = await getPhotoViewUrl(arr[i].photoUrl);
    res.push({
      ...arr[i],
      photo,
    });
  }
  return res;
};

// EventDto now has photoUrl, same private-bucket pattern as Family/Item.
export const getEventsPayload = async (arr) => {
  let res = [];
  for (let i = 0; i < arr.length; i++) {
    const photo = await getPhotoViewUrl(arr[i].photoUrl);
    res.push({
      ...arr[i],
      photo,
    });
  }
  return res;
};

export const getItemsPayload = async (arr) => {
  let res = [];
  for (let i = 0; i < arr.length; i++) {
    const photo = await getPhotoViewUrl(arr[i].photoUrl);
    res.push({
      ...arr[i],
      photo,
    });
  }
  return res;
};

export const getRemindersPayload = (arr) => {
  return arr.reduce((res, ele) => {
    res.push({
      id: ele.id,
      title: ele.medicatonName,
      start: utils.parseDate(ele.startDate),
      end: utils.parseDate(ele.startDate),
      className: utils.getRandomClass(),
    });
    return res;
  }, []);
};

export const getOptionsListPayload = (arr) => {
  return arr.reduce((res, ele, idx) => {
    res.push({
      id: idx + 1,
      name: ele.value,
    });
    return res;
  }, []);
};

// No more timeModel/taskModel join — the backend already returns the direct
// title/startTime/endTime/description fields.
export const getSchedulesPayload = (arr) => arr;

export const getSongsPayload = async (arr) => {
  let res = [];
  for (let i = 0; i < arr.length; i++) {
    // url is a stored S3 key (same private bucket as photos), needs the
    // same presigned-URL resolution to actually be playable.
    const songReference = await getPhotoViewUrl(arr[i].url);
    res.push({
      ...arr[i],
      songReference,
    });
  }
  return res;
};

export const getColorOptionsListPayload = (arr) => {

  return arr.reduce((res, ele) => {
    res.push({
      id: ele.id,
      name: ele.name,
    });
    return res;
  }, []);
};

//--------------------------------------------------------- DASHBOARD MODULE ----------------------------------------------------------------
// Get Payloads
export const getPieCharts = (arr) => {
  let labels = [];
  let series = [];
  let types = [];
  const myObj =
    arr &&
    arr.reduce((res, ele, idx) => {
      labels.push(`${ele.count}%`);
      series.push({
        value: Number(ele.count),
        className: `${colorNames[idx]}-pie`,
      });
      types.push({
        id: ele.id,
        name: ele.name,
        color: colorNames[idx],
      });
      res["labels"] = labels;
      res["series"] = series;
      res["types"] = types;
      return res;
    }, {});
  return {
    data: {
      labels: myObj.labels,
      series: myObj.series,
    },
    types: myObj.types,
  };
};

export const getLineCharts = (arr) => {
  let labels = ["06", "07", "08", "09", "10", "11", "12", "13", "14", "15"];
  let series = [];
  const myObj =
    arr &&
    arr.reduce((res, ele) => {
      const result = ele.count.reduce((res1, ele1, idx1) => {
        res1.push({
          value: Number(ele1),
          className: `${colorNames[idx1]}-pie`,
        });
        return res1;
      }, []);
      series.push(result);
      res["labels"] = labels;
      res["series"] = series;
      return res;
    }, {});
  return {
    data: {
      labels: myObj.labels,
      series: myObj.series,
    },
  };
};

//--------------------------------------------------------- PROFILE MODULE ----------------------------------------------------------------
// Get Payloads
// ProfileDto from Profile/GetMyProfile: {userId, firstName, lastName, email,
// role, roleName, organizationId, doctorId, subscriptionStatus, cosmaProduct}.
// diagnosis/stages/sex/religion lists had no replacement found (CustomerUi
// controller is gone) — dropped rather than guessed at.
export const getProfile = (dto = {}) => {
  return {
    ...dto,
    userName: [dto.firstName, dto.lastName].filter(Boolean).join(" "),
  };
};

export const getProfiles = (data) => {
  return data;
};

export const getOtherProfiles = (data) => {
  const currentEmail = services.getUserEmail();
  const myData = data.find((e) => e.userName === currentEmail || e.email === currentEmail);
  return myData || data[0];
};

export const getAccounts = (data) => {
  const currentEmail = services.getUserEmail();
  const myData = data.find((e) => e.email === currentEmail);
  return myData || data[0];
};

export const getAllProfiles = (orgs, doctors, patients, feedbacks) => {
  return {
    orgs: orgs.data,
    doctors: doctors.data,
    patients: patients.data,
    feedbacks: feedbacks.data,
  };
};

export const getDates = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  const fmt = (d) => d.toISOString().split("T")[0];
  return {
    fromDate: fmt(from),
    toDate: fmt(to),
  };
};

export const getReports = (data) => {
  const icons = [
    "work",
    "translate",
    "folder-mydrive",
    "autorenew",
    "timer-send",
    "settings-input-svideo",
  ];
  const colors = ["primary", "secondary", "info", "success", "warning", "error"];

  if (!Array.isArray(data) || data.length === 0) return { categories: [] };

  const categories = data.reduce((res, ele) => {
    const mainCategoryList = ele?.mainCategoryList || [];
    mainCategoryList.forEach((item) => {
      res.push({
        id: item?.category?.categoryId,
        name: item?.category?.categoryName,
        icon: utils.getRandomValueFromArr(icons),
        color: utils.getRandomValueFromArr(colors),
        count: ele?.averageScore?.score != null ? ele.averageScore.score.toFixed(2) : "0.00",
      });
    });
    return res;
  }, []);

  return { categories };
};

//--------------------------------------------------------- USERS MODULE ----------------------------------------------------------------